/**
 * Chatwoot Pulse — React Query hook for Chatwoot conversation counts
 *
 * Calls control_plane.api.chatwoot.get_dashboard_data via raw fetch()
 * (NOT frappeMethod — that routes to Tenant Server, not Control Plane).
 * Returns open conversation count and unassigned count for dashboard tiles.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getCPToken } from "@/lib/api/controlPlaneClient";
import { useAuthStore } from "@/lib/store/authStore";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

// ── Types ────────────────────────────────────────────────

export interface ChatwootPulseResponse {
  success: boolean;
  has_chatwoot_account: boolean;
  open_count: number;
  unassigned_count: number;
  mine_count: number;
}

const EMPTY_RESPONSE: ChatwootPulseResponse = {
  success: true,
  has_chatwoot_account: false,
  open_count: 0,
  unassigned_count: 0,
  mine_count: 0,
};

// ── Fetcher ──────────────────────────────────────────────

async function fetchChatwootPulse(tenantName: string): Promise<ChatwootPulseResponse> {
  try {
    const token = getCPToken();
    if (!token) return EMPTY_RESPONSE;
    const response = await fetch(
      `${CONTROL_PLANE_URL}/api/method/control_plane.api.chatwoot.get_dashboard_data`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          access_token: token,
          tenant_name: tenantName,
        }),
      }
    );

    if (!response.ok) return EMPTY_RESPONSE;

    const data = await response.json();
    return (data.message as ChatwootPulseResponse) ?? EMPTY_RESPONSE;
  } catch {
    return EMPTY_RESPONSE;
  }
}

// ── Hook ─────────────────────────────────────────────────

export function useChatwootPulse(): UseQueryResult<ChatwootPulseResponse, Error> & {
  isFetched: boolean;
} {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const currentTenant = useAuthStore((s) => s.currentTenant);

  const result = useQuery({
    queryKey: ["chatwoot-pulse", currentTenant],
    queryFn: () => fetchChatwootPulse(currentTenant ?? ""),
    staleTime: 5 * 60 * 1000, // 5 minutes (longer than ERP pulse — Chatwoot changes less frequently)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 1,
    enabled: hasHydrated && isAuthenticated && !!currentTenant,
  });

  return { ...result, isFetched: result.isFetched };
}
